<template>
  <d-modal
    :active="isActive"
    :width="700"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-xl font-semibold text-secondary-500">
          {{ category.name }}
        </h3>
        <h2 class="text-4xl font-bold">
          Access Groups
        </h2>
      </div>

      <!-- Add Group Form -->
      <div class="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 class="text-lg font-semibold mb-4">Add New Access Group</h4>
        <form @submit.prevent="saveRule()" class="space-y-4">
          <d-field label="Name">
            <d-input v-model="form.name" required />
          </d-field>
          
          <d-field label="Subject Area Access Rule">
            <d-select v-model="form.subjectAreaAccessRuleId" required>
              <option :value="null">
                Please select a value
              </option>
              <optgroup v-if="accessEmailRules.length > 0" label="Access By Email">
                <option v-for="accessRule in accessEmailRules" :key="accessRule.id" :value="accessRule.id">
                  {{ accessRule.identifier }}
                </option>
              </optgroup>
              <optgroup v-if="accessDomainRules.length > 0" label="Access By Email Domain">
                <option v-for="accessRule in accessDomainRules" :key="accessRule.id" :value="accessRule.id">
                  {{ accessRule.identifier }}
                </option>
              </optgroup>
            </d-select>
          </d-field>
          
          <div class="flex justify-end">
            <d-btn 
              type="submit" 
              color="primary"
              :loading="saving"
              :disabled="saving"
            >
              Add Group
            </d-btn>
          </div>
        </form>
      </div>

      <!-- Existing Access Groups -->
      <div class="mb-6">
        <h4 class="text-lg font-semibold mb-4">Current Access Groups</h4>
        
        <div v-if="loading" class="flex justify-center py-10">
          <span class="loading loading-spinner loading-md"></span>
        </div>
        
        <div v-else-if="accessGroups.length === 0" class="text-center py-8 text-gray-500">
          There are no access groups for this category.
        </div>
        
        <div v-else class="space-y-3">
          <div 
            v-for="group in accessGroups" 
            :key="group.id" 
            class="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50"
          >
            <div>
              <p class="font-medium">{{ group.name }}</p>
              <p class="text-sm text-gray-600">Access Rule: {{ getAccessRuleName(group.subjectAreaAccessRuleId) }}</p>
            </div>
            <d-btn
              size="sm"
              flat
              color="error"
              @click="confirmRemoveGroup(group)"
              :disabled="removing === group.id"
            >
              <d-icon icon="trash" />
              {{ removing === group.id ? 'Removing...' : 'Remove' }}
            </d-btn>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="total > pagination.pageSize" class="flex justify-center mt-6">
        <d-pagination 
          :total-items="total" 
          :per-page="pagination.pageSize" 
          v-model:current="pagination.page" 
        />
      </div>

      <!-- Modal Actions -->
      <div class="flex justify-end gap-3 pt-4 border-t">
        <d-btn 
          flat 
          @click="handleClose"
        >
          Close
        </d-btn>
      </div>
    </div>

    <!-- Remove Confirmation Modal -->
    <confirm-modal
      :active="removeModalActive"
      :title="'Remove Access Group'"
      :message="`Are you sure you want to remove access group <strong>${groupToRemove?.name}</strong>?<br><br>This action cannot be undone.`"
      :confirm-text="'Remove Group'"
      @close="closeRemoveModal"
      @confirm="handleRemoveConfirmed"
    />
  </d-modal>
</template>

<script>
  import axios from 'axios';
  import { toast } from '@services/toast.service';
  import ConfirmModal from '../../Users/components/ConfirmModal.vue';

  export default {
    name: 'CategoryAccessModal',
    
    components: {
      ConfirmModal,
    },

    props: {
      active: {
        type: Boolean,
        default: false,
      },
      category: {
        type: Object,
        default: () => ({}),
      },
      subjectArea: {
        type: Object,
        default: () => ({}),
      },
    },

    emits: ['close', 'saved'],

    data() {
      return {
        form: {
          name: '',
          subjectAreaAccessRuleId: null,
        },
        defaultForm: {
          name: '',
          subjectAreaAccessRuleId: null,
        },
        saving: false,
        loading: false,
        removing: null,
        accessGroups: [],
        accessRules: [],
        total: 0,
        removeModalActive: false,
        groupToRemove: null,
        pagination: {
          page: 1,
          pageSize: 10,
          sortField: 'name',
          sortOrder: 'asc',
        },
      };
    },

    computed: {
      isActive() {
        return this.active;
      },

      accessEmailRules() {
        return this.accessRules.filter((m) => m.type === 'Email');
      },

      accessDomainRules() {
        return this.accessRules.filter((m) => m.type === 'Domain');
      },
    },

    watch: {
      pagination: {
        handler() {
          if (this.isActive) {
            this.getAccessGroups();
          }
        },
        deep: true,
      },

      active: {
        handler(newVal) {
          if (newVal) {
            this.loadData();
          }
        },
        immediate: true,
      },
    },

    methods: {
      async loadData() {
        await Promise.all([
          this.getAccessRules(),
          this.getAccessGroups(),
        ]);
      },

      async getAccessGroups() {
        if (!this.category?.id) return;
        
        this.loading = true;
        try {
          const { page, sortField, sortOrder, pageSize } = this.pagination;
          const params = [
            `sortDir=${sortField} ${sortOrder}`,
            `currentPage=${page}`,
            `pageSize=${pageSize}`,
            `categoryId=${this.category.id}`
          ].join('&');
          
          const { data } = await axios.get(`/api/subject-areas/categories/${this.category.id}/category-groups/paged?${params}`);
          this.accessGroups = data.resultItems || [];
          this.total = data.totalRecords || 0;
        } catch (error) {
          console.error('Error loading access groups:', error);
          toast.error('Failed to load access groups');
          this.accessGroups = [];
          this.total = 0;
        } finally {
          this.loading = false;
        }
      },

      async getAccessRules() {
        if (!this.subjectArea?.id) return;
        
        try {
          const { data } = await axios.get(`/api/subject-areas/${this.subjectArea.id}/access-rules`);
          this.accessRules = data || [];
        } catch (error) {
          console.error('Error loading access rules:', error);
          toast.error('Failed to load access rules');
          this.accessRules = [];
        }
      },

      async saveRule() {
        if (!this.category?.id) return;
        
        this.saving = true;
        try {
          const { data } = await axios.post(`/api/subject-areas/categories/${this.category.id}/category-groups`, this.form);
          await this.getAccessGroups(); // Reload the list
          this.form = JSON.parse(JSON.stringify(this.defaultForm));
          toast.success('Access group added successfully');
          this.$emit('saved');
        } catch (error) {
          console.error('Error saving access group:', error);
          toast.error('Failed to add access group');
        } finally {
          this.saving = false;
        }
      },

      confirmRemoveGroup(group) {
        this.groupToRemove = group;
        this.removeModalActive = true;
      },

      closeRemoveModal() {
        this.removeModalActive = false;
        this.groupToRemove = null;
      },

      async handleRemoveConfirmed() {
        if (!this.groupToRemove) return;
        
        this.removing = this.groupToRemove.id;
        try {
          await axios.delete(`/api/subject-areas/categories/category-groups/${this.groupToRemove.id}`);
          await this.getAccessGroups(); // Reload the list
          toast.success('Access group removed successfully');
          this.$emit('saved');
        } catch (error) {
          console.error('Error removing access group:', error);
          toast.error('Failed to remove access group');
        } finally {
          this.removing = null;
          this.closeRemoveModal();
        }
      },

      getAccessRuleName(ruleId) {
        const rule = this.accessRules.find(r => r.id === ruleId);
        return rule ? rule.identifier : 'Unknown';
      },

      handleClose() {
        this.$emit('close');
      },
    },
  };
</script>